import { formatPrice } from '../lib/price';
import React, { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import MemberBadge from '../components/MemberBadge';
import BookingSharesPanel from '../components/BookingSharesPanel';
import { useAuth } from '../App';
import { getLevelLabel, getLevelBadgeClass } from '../utils/level';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { 
  CalendarBlank, 
  Users, 
  CreditCard,
  ArrowRight,
  Clock,
  MapPin,
  Barbell,
  Lightning,
  SpinnerGap
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import PadlrCoachCard from '../components/PadlrCoachCard';
import EventBookingCard from '../components/EventBookingCard';
import { OpenMatchFromBooking } from '../components/OpenMatchFromBooking';

const CustomerDashboard = () => {
  const { user, t, language, API } = useAuth();
  const navigate = useNavigate();
  const isTrainer = user?.role === 'trainer';
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [recommendedMatches, setRecommendedMatches] = useState({ matches: [], ai_summary: '' });
  const [trainerRecs, setTrainerRecs] = useState({ slots: [], ai_summary: '', generated: false });
  const [trainerRecsLoading, setTrainerRecsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [bookingSlot, setBookingSlot] = useState(null); // trainer slot being booked
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null); // friendly message shown inside the dialog
  const [cancelConfirm, setCancelConfirm] = useState(null); // {bookingId, hoursUntil}
  const [openMatchFor, setOpenMatchFor] = useState(null);

  const dateLocale = language === 'de' ? de : enUS;
  const L = (en, deu) => language === 'de' ? deu : en;

  useEffect(() => {
    loadDashboardData();
    loadTrainerRecommendations();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, matchesRes, eventsRes] = await Promise.all([
        API.get('/bookings'),
        API.get('/matches/recommended'),
        API.get('/my/events').catch(() => ({ data: [] })),
      ]);
      
      const nowKey = format(new Date(), "yyyy-MM-dd'T'HH:mm");
      const k = (b) => `${b.date}T${b.start_time || '00:00'}`;
      const upcoming = [
        ...bookingsRes.data.filter(b => b.status !== 'cancelled'),
        ...(eventsRes.data || []).map(e => ({ ...e, _event: true })),
      ]
        .filter(b => `${b.date}T${b.end_time || b.start_time || '23:59'}` >= nowKey)
        .sort((a, b) => k(a).localeCompare(k(b)))
        .slice(0, 5);
      setUpcomingBookings(upcoming);
      setRecommendedMatches(matchesRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTrainerRecommendations = async () => {
    try {
      setTrainerRecsLoading(true);
      const { data } = await API.get('/trainer-recommendations');
      setTrainerRecs(data);
    } catch (error) {
      console.error('Error loading trainer recommendations:', error);
    } finally {
      setTrainerRecsLoading(false);
    }
  };

  const hoursUntilBooking = (booking) => {
    const dt = new Date(booking.date + 'T' + booking.start_time);
    return (dt - new Date()) / (1000 * 60 * 60);
  };

  const canCancelBooking = (booking) => {
    if (booking.status === 'cancelled') return false;
    return hoursUntilBooking(booking) >= 24;
  };

  const handleCancelClick = (booking) => {
    setCancelConfirm({
      booking,
      onConfirm: async () => {
        try {
          const { data } = await API.put(`/bookings/${booking.id}/cancel`);
          if (data?.refund?.amount) {
            toast.success(L(
              `Booking cancelled — ${data.refund.amount.toFixed(2)} CHF refund on its way (1–3 business days).`,
              `Buchung storniert — ${data.refund.amount.toFixed(2)} CHF Rückerstattung ist unterwegs (1–3 Werktage).`
            ));
          } else {
            toast.success(L('Booking cancelled', 'Buchung storniert'));
          }
          loadDashboardData();
        } catch (e) {
          toast.error(e.response?.data?.detail || L('Cancellation failed', 'Stornierung fehlgeschlagen'));
        } finally {
          setCancelConfirm(null);
        }
      },
    });
  };

  const confirmTrainerBooking = async () => {
    if (!bookingSlot) return;
    if (!bookingSlot.court_id) {
      setBookingError(L('No free court for this slot anymore.', 'Kein freier Platz für diesen Slot verfügbar.'));
      return;
    }
    setBookingLoading(true);
    setBookingError(null);
    try {
      await API.post('/bookings', {
        court_id: bookingSlot.court_id,
        date: bookingSlot.date,
        start_time: bookingSlot.start_time,
        duration_minutes: 60,
        booking_type: 'training',
        trainer_id: bookingSlot.trainer_id,
      });
      toast.success(L('Training booked!', 'Training gebucht!'));
      setBookingSlot(null);
      setBookingError(null);
      // Refresh data
      loadDashboardData();
      loadTrainerRecommendations();
    } catch (e) {
      // Show the backend message inside the dialog so the customer can read the full explanation
      const msg = e.response?.data?.detail || L('Booking failed', 'Buchung fehlgeschlagen');
      setBookingError(msg);
    } finally {
      setBookingLoading(false);
    }
  };

  const getLevelBadge = (level) => getLevelBadgeClass(level);
  const getLevelLabelLocal = (level) => getLevelLabel(level, language);

  return (
    isTrainer ? <Navigate to="/trainer/schedule" replace /> :
    <div className="space-y-8 animate-fade-in" data-testid="customer-dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-2">
              <span>{language === 'de' ? `Hallo, ${user.name}` : `Hello, ${user.name}`}</span>
              <MemberBadge user={user} size={30} className="-mt-0.5" />
            </span>
          </h1>
          <p className="text-slate-500">
            {language === 'de' 
              ? 'Willkommen bei Padelarena Wädenswil'
              : 'Welcome to Padelarena Wädenswil'}
          </p>
        </div>
        <Link to="/book" className="btn-primary flex items-center gap-2" data-testid="quick-book-btn">
          <CalendarBlank size={18} weight="bold" />
          {t('bookCourt')}
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="stat-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="label-text">{t('playerLevel')}</span>
          </div>
          <p className={`level-badge ${getLevelBadge(user.player_level)} text-3xl font-black !px-4 !py-2`}>{user.player_level}</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="stat-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="label-text">{t('credits')}</span>
            <CreditCard size={20} className="text-emerald-600" weight="duotone" />
          </div>
          <p className="text-4xl font-black text-emerald-600">{user.credits_balance?.toFixed(0)}</p>
          <p className="text-slate-500 text-sm">CHF</p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="stat-card cursor-pointer hover:shadow-md transition"
          onClick={() => navigate('/profile?tab=bookings')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/profile?tab=bookings'); }}
          data-testid="dashboard-my-bookings-card"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="label-text">{t('myBookings')}</span>
            <CalendarBlank size={20} className="text-green-600" weight="duotone" />
          </div>
          <p className="text-4xl font-black text-green-600">{upcomingBookings.length}</p>
          <p className="text-slate-500 text-sm">{language === 'de' ? 'Kommende' : 'Upcoming'}</p>
        </motion.div>

        {!isTrainer && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="stat-card cursor-pointer hover:shadow-md transition"
            onClick={() => navigate('/matches')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/matches'); }}
            data-testid="dashboard-open-matches-card"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="label-text">{t('openMatches')}</span>
              <Users size={20} className="text-blue-600" weight="duotone" />
            </div>
            <p className="text-4xl font-black text-slate-900">{recommendedMatches.matches?.length || 0}</p>
            <p className="text-slate-500 text-sm">{language === 'de' ? 'Verfügbar' : 'Available'}</p>
          </motion.div>
        )}
      </div>

      {/* Padlr Coach banner — HIDDEN from customers (Feb 2026, repeat
          decision after regression report). This is the AI trainer-slot
          suggestion that pops up on rating drops. Endpoint + component
          stay live so we can flip the flag back to `true` in seconds. */}
      {false && !isTrainer && <PadlrCoachCard />}

      {/* AI Trainer Recommendations — TEMPORARILY HIDDEN (Sep 2026).
          Product decision: don't surface AI-picked lesson slots to customers
          for the moment. The endpoint + logic stay in the codebase so we can
          flip this back on in seconds by changing `false` to `true` below. */}
      {false && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="card-static p-6"
        data-testid="trainer-recommendations"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-xl flex items-center justify-center">
              <Lightning size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{L('Training Recommendations', 'Trainingsempfehlungen')}</h2>
              <p className="text-xs text-slate-400">{L('AI-powered suggestions for your next session', 'KI-Vorschläge für deine nächste Session')}</p>
            </div>
          </div>
        </div>

        {trainerRecsLoading ? (
          <div className="flex items-center justify-center py-8 gap-3">
            <SpinnerGap size={20} className="text-emerald-500 animate-spin" />
            <span className="text-slate-400 text-sm">{L('Analyzing available slots...', 'Verfügbare Slots werden analysiert...')}</span>
          </div>
        ) : (
          <>
            {trainerRecs.ai_summary && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/60 p-4 rounded-xl mb-4" data-testid="ai-trainer-summary">
                <p className="text-sm text-slate-700 leading-relaxed">{trainerRecs.ai_summary}</p>
              </div>
            )}

                {trainerRecs.slots?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {trainerRecs.slots.slice(0, 6).map((slot, idx) => (
                  <button
                    key={`${slot.trainer_id}-${slot.date}-${slot.start_time}`}
                    onClick={() => setBookingSlot(slot)}
                    className="group bg-white border border-slate-200 rounded-xl p-3 hover:border-emerald-400 hover:shadow-md transition-all text-left disabled:opacity-50"
                    disabled={!slot.court_id}
                    data-testid={`trainer-slot-${idx}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500 transition-colors">
                        <Barbell size={14} className="text-emerald-600 group-hover:text-white transition-colors" />
                      </div>
                      <span className="text-sm font-bold text-slate-900 truncate">{slot.trainer_name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <CalendarBlank size={12} />
                        {(() => { try { return format(new Date(slot.date + 'T00:00:00'), 'EEE, d. MMM', { locale: dateLocale }); } catch { return slot.date; } })()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {slot.start_time} - {slot.end_time}
                      </span>
                    </div>
                    <p className="mt-2 text-[11px] font-semibold text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      {L('Click to book →', 'Zum Buchen klicken →')}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-sm text-center py-4">{L('No open trainer slots this week', 'Keine freien Trainerslots diese Woche')}</p>
            )}

            {trainerRecs.total_available > 6 && (
              <p className="text-xs text-slate-400 mt-3 text-center">
                {L(`${trainerRecs.total_available} total slots available this week`, `${trainerRecs.total_available} Slots diese Woche verfügbar`)}
              </p>
            )}
          </>
        )}
      </motion.div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Bookings */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card-static p-6 cursor-pointer hover:shadow-md transition-shadow"
          onClick={(e) => { if (!e.target.closest('button, a, input, [role="dialog"]')) navigate('/profile?tab=bookings'); }}
          data-testid="dashboard-bookings-box"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">{t('myBookings')}</h2>
            <Link to="/profile?tab=bookings" className="text-emerald-600 text-sm font-medium hover:underline flex items-center gap-1" data-testid="dashboard-all-bookings-link">
              {t('all')} <ArrowRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : upcomingBookings.length === 0 ? (
            <div className="text-center py-8">
              <CalendarBlank size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
              <p className="text-slate-500">
                {language === 'de' ? 'Keine kommenden Buchungen' : 'No upcoming bookings'}
              </p>
              <Link to="/book" className="btn-primary mt-4 inline-block text-sm">
                {t('bookCourt')}
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingBookings.map((booking) => {
                if (booking._event) return <EventBookingCard key={`${booking.kind}-${booking.id}`} ev={{ ...booking, link: null }} language={language} dateLocale={dateLocale} />;
                const cancellable = canCancelBooking(booking);
                const hours = Math.round(hoursUntilBooking(booking));
                return (
                <div 
                  key={booking.id} 
                  className="bg-slate-50 border border-slate-200 p-4 rounded-lg"
                  data-testid={`booking-${booking.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-900 truncate">{booking.court_name}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <CalendarBlank size={14} />
                          {format(new Date(booking.date), 'PP', { locale: dateLocale })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {booking.start_time} - {booking.end_time}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1" data-testid={`booking-status-${booking.id}`}>
                      <span className="px-2 py-1 text-xs font-bold uppercase rounded-lg bg-green-50 text-green-600">
                        {language === 'de' ? 'Bestätigt' : 'Confirmed'}
                      </span>
                      <span className={`text-[10px] font-semibold ${booking.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}
                        data-testid={`booking-payment-${booking.id}`}>
                        {booking.payment_status === 'paid'
                          ? (language === 'de' ? 'Bezahlt' : 'Paid')
                          : (language === 'de' ? 'Zahlung offen' : 'Payment open')}
                      </span>
                    </div>
                  </div>
                  {/* Cancellation control + 24h policy */}
                  {cancellable ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelClick(booking)}
                      className="mt-3 w-full sm:w-auto text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`cancel-booking-${booking.id}`}
                    >
                      {L('Cancel (free)', 'Stornieren (kostenlos)')}
                    </Button>
                  ) : (
                    <p className="mt-3 text-xs text-slate-500" data-testid={`cancel-disabled-${booking.id}`}>
                      {booking.match_id ? L(
                        'Open match: cancel it on the Matches page (possible up to 8h before the start).',
                        'Open Match: Absage über die Match-Seite (bis 8h vor Beginn möglich).'
                      ) : L(
                        `Cancellation no longer possible — booking is in ${hours}h. Free cancellation is only allowed up to 24h before the start.`,
                        `Stornierung nicht mehr möglich — die Buchung beginnt in ${hours}h. Kostenlose Stornierung ist nur bis 24h vor Beginn möglich.`
                      )}
                    </p>
                  )}
                  {/* Split-payment shares — customer can pay their own unpaid share online */}
                  {booking.status !== 'cancelled' && !booking.match_id && (booking.num_players || 1) > 1 && (
                    <div className="mt-4">
                      <BookingSharesPanel bookingId={booking.id} />
                    </div>
                  )}
                  {booking.status !== 'cancelled' && !booking.match_id && booking.booking_type !== 'training'
                    && booking.payment_status !== 'paid' && (booking.num_players || 1) > 1 && booking.user_id === user.id && (
                    <Button variant="outline" size="sm" onClick={() => setOpenMatchFor(booking)}
                      className="mt-3 w-full sm:w-auto text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      data-testid={`open-as-match-${booking.id}`}>
                      <Users size={14} className="mr-1" /> {L('Find players (open match)', 'Spieler suchen (Open Match)')}
                    </Button>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </motion.div>

        {/* AI Recommended Matches */}
        {!isTrainer && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card-static p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900">{t('openMatches')}</h2>
            <Link to="/matches" className="text-emerald-600 text-sm font-medium hover:underline flex items-center gap-1">
              {t('all')} <ArrowRight size={14} />
            </Link>
          </div>

          {/* AI Summary */}
          {recommendedMatches.ai_summary && (
            <div className="bg-blue-50 border border-blue-500/30 p-4 rounded-lg mb-4">
              <p className="text-sm text-blue-600 font-medium mb-1">AI Recommendation</p>
              <p className="text-slate-900/90 text-sm">{recommendedMatches.ai_summary}</p>
            </div>
          )}

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-slate-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : recommendedMatches.matches?.length === 0 ? (
            <div className="text-center py-8">
              <Users size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
              <p className="text-slate-500">
                {language === 'de' ? 'Keine passenden Spiele verfügbar' : 'No matching games available'}
              </p>
              <Link to="/matches" className="btn-primary mt-4 inline-block text-sm">
                {t('createMatch')}
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recommendedMatches.matches?.slice(0, 3).map((match) => (
                <Link 
                  key={match.id} 
                  to="/matches"
                  className="block bg-slate-50 border border-slate-200 p-4 rounded-lg hover:border-emerald-500/30 transition-colors"
                  data-testid={`match-${match.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-slate-900 truncate">{match.court_name}</p>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <CalendarBlank size={14} />
                          {format(new Date(match.date), 'PP', { locale: dateLocale })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {match.start_time}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2">
                        <span className="text-xs text-slate-500">
                          {t('level')}: {match.min_level}-{match.max_level}
                        </span>
                        <span className="text-xs text-emerald-600">
                          {match.max_players - match.current_players.length} {t('spotsLeft')}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-emerald-600 font-bold">{formatPrice(match.cost_per_player)} CHF</p>
                      <p className="text-xs text-slate-500">
                        {match.current_players.length}/{match.max_players} {t('players')}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.div>
        )}
      </div>

      {/* Training booking confirmation */}
      <Dialog open={!!bookingSlot} onOpenChange={(open) => { if (!open) { setBookingSlot(null); setBookingError(null); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm" data-testid="training-booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Book Training', 'Training buchen')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {L('Confirm this training lesson with your trainer', 'Trainingsstunde mit dem Trainer bestätigen')}
            </DialogDescription>
          </DialogHeader>
          {bookingSlot && (
            <div className="space-y-4 mt-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
                <p className="text-sm">
                  <span className="font-semibold text-slate-600">{L('Trainer', 'Trainer')}:</span>{' '}
                  <span className="text-slate-900 font-bold">{bookingSlot.trainer_name}</span>
                </p>
                <p className="text-sm">
                  <span className="font-semibold text-slate-600">{L('Date', 'Datum')}:</span>{' '}
                  {(() => { try { return format(new Date(bookingSlot.date + 'T00:00:00'), 'EEEE, d. MMMM yyyy', { locale: dateLocale }); } catch { return bookingSlot.date; } })()}
                </p>
                <p className="text-sm">
                  <span className="font-semibold text-slate-600">{L('Time', 'Zeit')}:</span>{' '}
                  {bookingSlot.start_time} - {bookingSlot.end_time} <span className="text-slate-400">({L('60 min', '60 Min')})</span>
                </p>
                <p className="text-sm pt-1 border-t border-emerald-200 mt-2">
                  <span className="font-semibold text-slate-600">{L('Price', 'Preis')}:</span>{' '}
                  <span className="text-emerald-700 font-bold">100.00 CHF</span>
                </p>
                <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5 mt-2">
                  <MapPin size={14} className="text-blue-600 flex-shrink-0" />
                  <span className="text-xs text-blue-700 font-medium">
                    {L('Pay at the club (cash/card)', 'Zahlung im Club (Bar/Karte)')}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
                  {L('ℹ️ Free cancellation up to 24 hours before the lesson.',
                     'ℹ️ Kostenlose Stornierung bis 24 Stunden vor der Lektion.')}
                </p>
              </div>

              {bookingError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="training-booking-error">
                  <p className="text-sm font-bold text-red-700 mb-1">
                    {L('Booking not possible', 'Buchung nicht möglich')}
                  </p>
                  <p className="text-sm text-red-700 leading-relaxed whitespace-pre-line">
                    {bookingError}
                  </p>
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => { setBookingSlot(null); setBookingError(null); }} className="flex-1 border-slate-200 text-slate-600" data-testid="training-booking-cancel">
                  {bookingError ? L('Close', 'Schliessen') : L('Cancel', 'Abbrechen')}
                </Button>
                {!bookingError && (
                  <Button
                    onClick={confirmTrainerBooking}
                    disabled={bookingLoading}
                    className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold"
                    data-testid="training-booking-confirm"
                  >
                    {bookingLoading ? '...' : L('Book now', 'Jetzt buchen')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancellation Confirmation Dialog */}
      {openMatchFor && (
        <OpenMatchFromBooking booking={openMatchFor} open={!!openMatchFor} onClose={() => setOpenMatchFor(null)}
          onDone={() => { setOpenMatchFor(null); loadDashboardData(); }} />
      )}

      <Dialog open={!!cancelConfirm} onOpenChange={(open) => { if (!open) setCancelConfirm(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm" data-testid="cancel-confirm-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">
              {L('Cancel booking?', 'Buchung stornieren?')}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {L(
                'You can cancel free of charge up to 24 hours before the start. After that the booking is final.',
                'Du kannst bis 24 Stunden vor Beginn kostenlos stornieren. Danach ist die Buchung verbindlich.'
              )}
            </DialogDescription>
          </DialogHeader>
          {cancelConfirm?.booking && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1">
              <p className="font-bold text-slate-900">{cancelConfirm.booking.court_name}</p>
              <p className="text-slate-600">
                {format(new Date(cancelConfirm.booking.date), 'PP', { locale: dateLocale })} ·{' '}
                {cancelConfirm.booking.start_time} – {cancelConfirm.booking.end_time}
              </p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setCancelConfirm(null)}
              className="flex-1"
              data-testid="cancel-confirm-keep"
            >
              {L('Keep booking', 'Behalten')}
            </Button>
            <Button
              onClick={() => cancelConfirm?.onConfirm?.()}
              className="flex-1 bg-red-500 text-white hover:bg-red-600"
              data-testid="cancel-confirm-yes"
            >
              {L('Yes, cancel', 'Ja, stornieren')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDashboard;
